import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Archive, Building2, CheckCircle2, Download } from 'lucide-react';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import Callout from '../Callout';
import './DeleteCompanyModal.css';

// Archive download remains optional, per the owner's earlier decision.
export default function DeleteCompanyModal({ show, onHide, companyId, companyName, onDeleted }) {
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        setConfirmText(''); setBusy(false); setExporting(false); setExported(false); setError('');
    }, [show, companyId]);
    const canDelete = Boolean(companyName) && confirmText === companyName && !busy && !exporting;
    const handleExport = async () => {
        setExporting(true); setError('');
        try {
            const response = await axiosInstance.get(getApiUrl(`${companyId}/offboarding/export`), { responseType: 'blob', timeout: 300000 });
            const url = URL.createObjectURL(response.data);
            const anchor = document.createElement('a');
            anchor.href = url; anchor.download = `ledgerloop-arkiv-${companyId}.zip`;
            anchor.click(); URL.revokeObjectURL(url); setExported(true);
        } catch {
            setError('Arkivet kunde inte skapas. Försök igen eller kontakta oss för hjälp med exporten. Företaget har inte stängts.');
        } finally { setExporting(false); }
    };
    const handleDelete = async () => {
        if (!canDelete) return;
        setBusy(true); setError('');
        try {
            const { data } = await axiosInstance.delete(getApiUrl(`auth/company/${companyId}`), { data: { confirmationName: confirmText } });
            if (!data.closure_id || !data.companyClosed) throw new Error('Closure receipt missing');
            onDeleted?.(companyId, data); onHide?.();
        } catch (failure) {
            setError(failure?.response?.data?.code === 'RECENT_LOGIN_REQUIRED'
                ? 'Logga ut och logga in igen innan du avslutar företaget.'
                : failure?.response?.data?.message || 'Avslutet kunde inte bekräftas. Kontrollera Företagsavslut innan du försöker igen.');
        } finally { setBusy(false); }
    };
    return <Modal show={show} onHide={busy || exporting ? undefined : onHide} centered
        dialogClassName="company-delete-dialog" contentClassName="company-delete-modal"
        aria-labelledby="company-delete-title" aria-describedby="company-delete-name">
        <Modal.Header closeButton={!busy && !exporting}>
            <div className="company-delete-modal__heading">
                <span className="company-delete-modal__company-icon"><Building2 size={22} strokeWidth={1.75} aria-hidden="true" /></span>
                <div>
                    <Modal.Title id="company-delete-title">Avsluta företag</Modal.Title>
                    <p id="company-delete-name" className="company-delete-modal__name">{companyName}</p>
                </div>
            </div>
        </Modal.Header>
        <Modal.Body>
            <section className="company-delete-modal__archive" aria-labelledby="company-delete-archive-title">
                <div className="company-delete-modal__archive-title">
                    <Archive size={17} strokeWidth={1.75} aria-hidden="true" />
                    <h3 id="company-delete-archive-title">Spara en kopia</h3>
                    <span>Valfritt</span>
                </div>
                <p>Ladda ner ett arkiv med företagets underlag innan du fortsätter. Företaget ansvarar för att spara de handlingar som behöver bevaras.</p>
                <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={busy || exporting}>
                    {exporting ? <Spinner animation="border" size="sm" aria-hidden="true" /> : <Download size={15} aria-hidden="true" />}
                    {exporting ? 'Skapar arkiv…' : exported ? 'Ladda ner arkivet igen' : 'Ladda ner arkiv'}
                </Button>
                {exported && <p className="company-delete-modal__downloaded" role="status">
                    <CheckCircle2 size={16} aria-hidden="true" />
                    Nedladdningen har startats. Kontrollera att arkivet innehåller dina underlag.
                </p>}
            </section>
            <Form.Group className="company-delete-modal__confirmation" controlId="company-closure-confirm">
                <Form.Label>Skriv företagsnamnet för att bekräfta</Form.Label>
                <Form.Control value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder={companyName}
                    autoComplete="off" spellCheck={false} disabled={busy || exporting} />
            </Form.Group>
            <p className="company-delete-modal__hint">Du kan följa raderingen under Företagsavslut.</p>
            {error && <Callout tone="danger" role="alert" className="mb-0"><p>{error}</p></Callout>}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={busy || exporting}>Avbryt</Button>
            <Button variant="danger" size="sm" onClick={handleDelete} disabled={!canDelete}>
                {busy && <Spinner animation="border" size="sm" aria-hidden="true" />}
                {busy ? 'Stänger…' : 'Avsluta företag'}
            </Button>
        </Modal.Footer>
    </Modal>;
}
